import React, { useState, useEffect } from "react";
import { Empresa } from "../models/posto";
import {
  RelatorioEstoque,
  RelatorioEstoquePorTanque,
  RelatorioEstoqueResponse,
} from "../models/relatorioEstoque";
import { useApi } from "../components/api";
import {
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Slider,
  Tab,
  Tabs,
  Typography,
  withStyles,
} from "@material-ui/core";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { AvatarCombustivel } from "../components/avatarCombustivel";
import numeral from "numeral";
import { TabPanel } from "../components/TabPanel";
import { getCombustivelDefByNome } from "../models/combustivel";
import { EmpresaFilter } from "./dashboard/EmpresaFilter";

const IndicadorSlider = withStyles({
  root: {
    color: "#52af77",
    height: 6,
    cursor: "auto",
    padding: "0px",
  },
  thumb: {
    display: "none",
  },
  active: {},
  valueLabel: {
    left: "calc(-50% + 4px)",
  },
  track: {
    height: 6,
    borderRadius: 0,
  },
  rail: {
    height: 6,
    borderRadius: 0,
  },
})(Slider);

export const EstoquePage = () => {
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>(
    "Aguarde, carregando...",
  );
  const [retorno, setRetorno] = useState<{ [id: string]: RelatorioEstoque }>(
    {},
  );
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [empresasFiltradas, setEmpresasFiltradas] = useState<number[]>([]);
  const [tabIndex, setTabIndex] = React.useState(0);
  const [expandedEmpresas, setExpandedEmpresas] = useState<{
    [cod: number]: boolean;
  }>({});

  const { api } = useApi();

  useEffect(() => {
    const load = async () => {
      setLoadingMessage("Carregando dados...");
      try {
        const resp = await api.get<RelatorioEstoqueResponse>("estoque/atual");
        setRetorno(resp.data.data);
        const emps = Object.keys(resp.data.data).map(
          (x) =>
            ({
              codigo: resp.data.data[x].empresaCod,
              nome: resp.data.data[x].empresaNome,
            }) as Empresa,
        );
        setEmpresas(emps);
        const expanded: { [cod: number]: boolean } = {};
        emps.forEach((e) => (expanded[e.codigo] = true));
        setExpandedEmpresas(expanded);
      } catch (error) {}
      setLoadingMessage(undefined);
    };
    if (api) load();
  }, [api]);

  const handleTabsChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const toggleExpand = (cod: number) => {
    setExpandedEmpresas((prev) => ({ ...prev, [cod]: !prev[cod] }));
  };

  // empresas efetivamente exibidas
  const empresasVisiveis =
    empresasFiltradas.length > 0
      ? empresas.filter((e) => empresasFiltradas.includes(e.codigo))
      : empresas;

  const getEstoqueKey = (emp: Empresa) =>
    Object.keys(retorno).find((k) => retorno[k].empresaCod === emp.codigo) ??
    "";

  function getTanquesPorEmpresa(emp: Empresa): RelatorioEstoquePorTanque[] {
    const key = getEstoqueKey(emp);
    if (!key) return [];
    return Object.values(retorno[key].porTanque);
  }

  // soma de combustíveis across empresas visíveis
  const combustiveisSomados = (() => {
    const acc: { [nome: string]: RelatorioEstoquePorTanque } = {};
    empresasVisiveis.forEach((emp) => {
      const key = getEstoqueKey(emp);
      if (!key) return;
      Object.values(retorno[key].porCombustivel).forEach((c) => {
        if (acc[c.produtoNome]) {
          acc[c.produtoNome] = {
            ...acc[c.produtoNome],
            saldo: acc[c.produtoNome].saldo + c.saldo,
            capacidade: acc[c.produtoNome].capacidade + c.capacidade,
          };
        } else {
          acc[c.produtoNome] = { ...c, tanqueNro: 0 };
        }
      });
    });
    return Object.values(acc);
  })();

  const dataRef =
    empresasVisiveis.length > 0
      ? retorno[getEstoqueKey(empresasVisiveis[0])]?.dataRef
      : undefined;

  function renderTanqueItem(
    item: RelatorioEstoquePorTanque,
    keyPrefix: string,
  ) {
    return (
      <div key={`${keyPrefix}_${item.produtoCod}_${item.tanqueNro}`}>
        <ListItem>
          <ListItemAvatar>
            <AvatarCombustivel
              combustivelSigla={getCombustivelDefByNome(item.produtoNome).sigla}
            />
          </ListItemAvatar>
          <ListItemText
            primary={
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <b style={{ fontSize: "1.1rem" }}>
                  {numeral(item.saldo).format("(0)")} L.
                </b>
                <span>{item.capacidade}</span>
              </Grid>
            }
            secondary={
              <span>
                <IndicadorSlider
                  min={0}
                  max={item.capacidade}
                  value={item.saldo}
                />
                {item.tanqueNro > 0 && <span>TANQUE {item.tanqueNro}</span>}
              </span>
            }
          />
        </ListItem>
        <Divider />
      </div>
    );
  }

  return (
    <div>
      {loadingMessage ? (
        <CircularProgress />
      ) : (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 12px",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <span style={{ fontSize: "10px" }}>
              Atualizado em:{" "}
              {dataRef ? new Date(dataRef).toLocaleString("pt-BR") : ""}
            </span>
            {empresas.length > 1 && (
              <EmpresaFilter
                selected={empresasFiltradas}
                onChange={setEmpresasFiltradas}
                empresas={empresas}
              />
            )}
          </div>

          <Tabs
            value={tabIndex}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleTabsChange}
            variant="fullWidth"
          >
            <Tab label="Por Tanque" />
            <Tab label="Por Combustíveis" />
          </Tabs>

          <TabPanel value={tabIndex} index={0}>
            {empresasVisiveis.length === 1 ? (
              <List>
                {getTanquesPorEmpresa(empresasVisiveis[0]).map((t) =>
                  renderTanqueItem(t, String(empresasVisiveis[0].codigo)),
                )}
              </List>
            ) : (
              empresasVisiveis.map((emp) => (
                <div key={emp.codigo}>
                  <ListItem
                    button
                    onClick={() => toggleExpand(emp.codigo)}
                    style={{ backgroundColor: "#f5f5f5" }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2">{emp.nome}</Typography>
                      }
                    />
                    {expandedEmpresas[emp.codigo] ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </ListItem>
                  <Collapse
                    in={expandedEmpresas[emp.codigo]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List disablePadding>
                      {getTanquesPorEmpresa(emp).map((t) =>
                        renderTanqueItem(t, String(emp.codigo)),
                      )}
                    </List>
                  </Collapse>
                  <Divider />
                </div>
              ))
            )}
          </TabPanel>

          <TabPanel value={tabIndex} index={1}>
            <List>
              {combustiveisSomados.map((c) => renderTanqueItem(c, "comb"))}
            </List>
          </TabPanel>
        </div>
      )}
    </div>
  );
};
